import apiClient from './apiClient';

export interface Benchmark {
  id: number;
  user_id: string;
  youtube_id: string | null;
  input_audio_size: number | null;
  input_image_size: number | null;
  output_video_size: number | null;
  decode_time: number | null;
  probe_time: number | null;
  write_time: number | null;
  ffmpeg_time: number | null;
  read_time: number | null;
  upload_time: number | null;
  total_time: number | null;
  created_at: string; // ISO timestamp
}

export interface BenchmarksResponse {
  benchmarks: Benchmark[];
  total: number;
  limit: number;
  offset: number;
}

export interface BenchmarkStatistics {
  total_benchmarks: number;
  avg_ffmpeg_time: number | null;
  avg_upload_time: number | null;
  avg_total_time: number | null;
  min_ffmpeg_time: number | null;
  max_ffmpeg_time: number | null;
  min_upload_time: number | null;
  max_upload_time: number | null;
  min_total_time: number | null;
  max_total_time: number | null;
  avg_input_audio_size: number | null;
  avg_input_image_size: number | null;
  avg_output_video_size: number | null;
}

export interface TimeSeriesDataPoint {
  date: string; // YYYY-MM-DD
  avg_ffmpeg_time: number | null;
  avg_upload_time: number | null;
  avg_total_time: number | null;
  count: number;
}

export interface TimeSeriesResponse {
  data_points: TimeSeriesDataPoint[];
  period: string;
}

export interface BenchmarkDetail extends Benchmark {
  ffmpeg_command_hash: string | null;
  ffmpeg_command: string | null;
}

export interface GetBenchmarksParams {
  start_date?: string;
  end_date?: string;
  min_ffmpeg_time?: number;
  max_ffmpeg_time?: number;
  min_upload_time?: number;
  max_upload_time?: number;
  min_total_time?: number;
  max_total_time?: number;
  limit?: number;
  offset?: number;
}

export const adminBenchmarkService = {
  /**
   * Get benchmarks with optional filtering
   */
  getBenchmarks: async (params?: GetBenchmarksParams): Promise<BenchmarksResponse> => {
    const response = await apiClient.get('/payment/admin/benchmarks', { params });
    return response.data.data;
  },

  /**
   * Get benchmark statistics
   */
  getBenchmarkStatistics: async (startDate?: string, endDate?: string): Promise<BenchmarkStatistics> => {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get('/payment/admin/benchmarks/statistics', { params });
    return response.data.data;
  },

  /**
   * Get time-series data for charts
   */
  getTimeSeriesData: async (
    period: 'daily' | 'weekly' | 'monthly',
    startDate?: string,
    endDate?: string
  ): Promise<TimeSeriesResponse> => {
    const params: any = { period };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    const response = await apiClient.get('/payment/admin/benchmarks/time-series', { params });
    return response.data.data;
  },

  /**
   * Get detailed information about a specific benchmark
   */
  getBenchmarkDetails: async (benchmarkId: number): Promise<BenchmarkDetail> => {
    const response = await apiClient.get(`/payment/admin/benchmarks/${benchmarkId}`);
    return response.data.data;
  }
};
